import { useSelector } from 'react-redux'
import { Waypoint } from 'react-waypoint'
import { useCountUp } from 'react-countup'
import { useEffect, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { Format, Lock, Select, InlineTextLock } from '../../..'
import { selectHomeUuid } from '../../../../store/selectors/home'
import { Box, Text, Heading } from '@homebotapp/hb-react-component-catalog'
import { useTrackingContext } from '../../../../providers/tracking'
import { selectClientId } from '../../../../store/selectors/client'
import { selectFirstCEL } from '../../../../store/selectors/home/loans'
import { TrackingEventConstants } from '../../../../constants/tracking'
import { useTrackingDebounceSetters } from '../../../../hooks/tracking'
import { useGetExtraPrincipalPaymentSavingsQuery } from '../../../../store/slices/extraPrincipalPaymentSavings'

const DEFAULT_YRS = 12
const DEFAULT_ADD = 100
const DEFAULT_AMT = 47000

export const MSG = defineMessages({
  head: {
    id: 'Homeowner.interestCard.payMorePart1',
    defaultMessage: 'Tip: If you pay just {amount} more each month, you could save {total}'
  },
  arm: {
    id: 'Homeowner.interestCard.arm',
    defaultMessage:
      'This estimate could look different if your loan interest rate is not fixed, so be aware of how your rate could fluctuate periodically.'
  },
  body: {
    id: 'Homeowner.interestCard.payMorePart2',
    defaultMessage:
      "in interest over the rest of your loan. You'd also be done with the loan and no longer making payments {yearsEarlier}! {arm}"
  },
  yearsEarlier: {
    id: 'Homeowner.interestCard.payMorePart2.yearsEarlier',
    defaultMessage: '{years} years earlier'
  },
  extra: {
    id: 'Homeowner.interestCard.extraMonthlyPayment',
    defaultMessage: 'Extra monthly payment'
  }
})

export interface ExtraPrincipalPaymentsProps {
  withCurrentHomeData: boolean
}

export const ExtraPrincipalPayments = ({ withCurrentHomeData }: ExtraPrincipalPaymentsProps) => {
  const intl = useIntl()
  const loan = useSelector(selectFirstCEL)
  const homeUuid = useSelector(selectHomeUuid)
  const clientId = useSelector(selectClientId) as string
  const { trackingClient } = useTrackingContext()
  const [monthsSaved, setMonthsSaved] = useState<number>(0)
  const [scrolledIntoView, setScrolledIntoView] = useState(false)
  const [extraPayment, setExtraPayment] = useState<number>(DEFAULT_ADD)
  const [trackExtraPrincipalAdjusted] = useTrackingDebounceSetters(() => trackingClient?.extraPrincipalAdjusted())
  const [trackModuleViewed] = useTrackingDebounceSetters(
    () =>
      trackingClient?.moduleViewed({
        module_name: TrackingEventConstants.PAY_EXTRA_PRINCIPAL
      })
  )

  const {
    data: extraPrincipalPaymentSavings = [],
    isFetching,
    isSuccess,
    refetch
  } = useGetExtraPrincipalPaymentSavingsQuery(
    {
      clientId,
      homeUuid
    },
    {
      skip: !clientId || !homeUuid || !scrolledIntoView
    }
  )

  const { countUp: countUpPaymentSavings, update: updateCount } = useCountUp({
    end: 0,
    start: 0,
    duration: 1
  })

  useEffect(() => {
    !isFetching && isSuccess && refetch() // If we have already fetched and the loan updates - we need to refetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loan?.date, loan?.rate, loan?.amount, loan?.balance, loan?.termYears, loan?.adjustable, loan?.armYearsInitial])

  useEffect(() => {
    if (extraPrincipalPaymentSavings?.length) {
      const savings = extraPrincipalPaymentSavings.find(saving => saving.monthlyExtraPayment === extraPayment)

      savings && updateCount(savings.totalInterestSaved)
      savings && setMonthsSaved(savings.timeSavedInMonths)
    }
  }, [extraPrincipalPaymentSavings]) // eslint-disable-line

  return (
    <>
      <Waypoint onEnter={() => setScrolledIntoView(true)} bottomOffset='-50%' />
      <Heading as='h2' fontWeight='light'>
        {intl.formatMessage(MSG.head, {
          amount: (
            <Box display='inline-block'>
              <Select
                inline
                defaultValue={DEFAULT_ADD}
                aria-label='monthly payment'
                options={extraPrincipalPaymentSavings.map(savings => savings.monthlyExtraPayment)}
                optionValue={Format.currency}
                disabled={!withCurrentHomeData}
                onChange={value => {
                  setExtraPayment(parseInt(value))
                  trackExtraPrincipalAdjusted()

                  const savings = extraPrincipalPaymentSavings?.find(
                    saving => saving.monthlyExtraPayment === parseInt(value)
                  )

                  savings && updateCount(savings.totalInterestSaved)
                  savings && setMonthsSaved(savings.timeSavedInMonths)
                }}
              />
            </Box>
          ),
          total: (
            <Lock enabled={!withCurrentHomeData}>
              <Text
                size='xl'
                fontWeight='light'
                color={
                  (withCurrentHomeData ? (countUpPaymentSavings as number) : DEFAULT_AMT) >= 0 ? 'success' : 'danger'
                }
              >
                <Format.Currency
                  value={
                    Math.round((withCurrentHomeData ? (countUpPaymentSavings as number) : DEFAULT_AMT) / 500) * 500 // rounding to the nearest 500 dollars
                  }
                />
              </Text>
            </Lock>
          )
        })}
      </Heading>
      <Text size='sm' align='center'>
        {intl.formatMessage(MSG.body, {
          yearsEarlier: (
            <Text as='span' color='success'>
              {intl.formatMessage(MSG.yearsEarlier, {
                years: (
                  <InlineTextLock enabled={!withCurrentHomeData}>
                    {!withCurrentHomeData && DEFAULT_YRS}
                    {withCurrentHomeData && Math.round(monthsSaved / 12)}
                  </InlineTextLock>
                )
              })}
            </Text>
          ),
          arm: loan?.adjustable ? intl.formatMessage(MSG.arm) : ''
        })}
      </Text>
      <Waypoint onEnter={trackModuleViewed} bottomOffset='50%' />
    </>
  )
}
